import { useRef, useCallback, useMemo, useEffect } from "react";
import { Box, Button } from "@material-ui/core";

import ReactQuill, { Quill } from "react-quill";
import htmlEditButton from "quill-html-edit-button";
import ImageResize  from "quill-image-resize";

Quill.register({
  "modules/htmlEditButton": htmlEditButton,
  "modules/imageResize": ImageResize
});

const Font = Quill.import("formats/font");
Font.whitelist = ["Roboto", "Arial", "Verdana", "Helvetica", "Tahoma", "trebuchet-ms", "times-new-roman", "Georgia", "Garamond", "courier-new", "brush-script-mt"];
Quill.register(Font, true);

const Parchment = Quill.import("parchment");
const BackgroundStyle = new Parchment.Attributor.Style(
  "background",
  "background-color",
  {
    scope: Parchment.Scope.BLOCK,
  }
);
Quill.register(BackgroundStyle, true);

const BlockEmbed = Quill.import("blots/block/embed");
const Delta = Quill.import("delta");

class RawHtml extends BlockEmbed {
  static create(html) {
    const node = super.create();
    node.setAttribute("contenteditable", "false");
    node.innerHTML = html;
    return node;
  }
  static value(node) {
    return node.innerHTML;
  }
}
RawHtml.blotName = "raw-html";
RawHtml.tagName = "div";
RawHtml.className = "ql-raw-html";
Quill.register(RawHtml);

const COMMENT_NODE = 8;
const WP_OPEN = /^\s*wp:([a-z0-9-]+)\s*$/i;

function wrapWpBlocks(root, doc) {
  const elements = [root, ...root.querySelectorAll("*")];
  for (const el of elements) {
    if (el.closest(".ql-raw-html")) continue;
    const children = Array.from(el.childNodes);
    let i = 0;
    while (i < children.length) {
      const node = children[i];
      const open = node.nodeType === COMMENT_NODE && WP_OPEN.exec(node.data);
      if (open) {
        const closer = new RegExp(`^\\s*/wp:${open[1]}\\s*$`, "i");
        let j = i + 1;
        while (j < children.length) {
          const other = children[j];
          if (other.nodeType === COMMENT_NODE && closer.test(other.data)) break;
          j++;
        }
        if (j < children.length) {
          const wrapper = doc.createElement("div");
          wrapper.className = "ql-raw-html";
          for (let k = i; k <= j; k++) wrapper.appendChild(children[k]);
          el.insertBefore(wrapper, children[j + 1] || null);
          i = j + 1;
          continue;
        }
      }
      i++;
    }
  }
}

function wrapTables(root, doc) {
  for (const table of Array.from(root.querySelectorAll("table"))) {
    if (table.closest(".ql-raw-html")) continue;
    const wrapper = doc.createElement("div");
    wrapper.className = "ql-raw-html";
    table.parentNode.insertBefore(wrapper, table);
    wrapper.appendChild(table);
  }
}

function wrapRawHtml(html) {
  if (!html || typeof html !== "string") return html;
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
  const body = doc.body;
  wrapWpBlocks(body, doc);
  wrapTables(body, doc);
  return body.innerHTML;
}

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || "";

function Wysiwyg(props) {
  const { value, onChange, onSubmit, buttonCopy } = props;
  const quillRef = useRef(null);

  useEffect(() => {
    const quill = quillRef.current && quillRef.current.getEditor();
    if (!quill) return;
    const cb = quill.clipboard;
    const origPaste = cb.dangerouslyPasteHTML.bind(cb);
    cb.dangerouslyPasteHTML = function (index, html, source) {
      if (typeof index === "string") {
        return origPaste(wrapRawHtml(index), html);
      }
      return origPaste(index, wrapRawHtml(html), source);
    };
  }, []);

  const imageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      const credential = JSON.parse(localStorage.getItem("credential") || "{}");
      const token = credential.id_token;
      const objectName = `${Date.now()}-${file.name}`;

      const signingRes = await fetch(
        `${API_ENDPOINT}/admin/s3-signed-url?objectName=${encodeURIComponent(objectName)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { signedUrl } = await signingRes.json();

      await fetch(signedUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });

      const publicUrl = signedUrl.split("?")[0];

      const editor = quillRef.current.getEditor();
      const range = editor.getSelection(true);
      editor.insertEmbed(range.index, "image", publicUrl);
      editor.setSelection(range.index + 1);
    };
  }, []);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ "header": [1, 2, 3, 4, 5, 6, false] }],
        ["bold", "italic", "underline", "strike", "blockquote"],
        [{ "font": Font.whitelist }],
        [{"color": []}], [{ "align": [] }],
        [{"list": "ordered"}, {"list": "bullet"}, {"indent": "-1"}, {"indent": "+1"}],
        ["link", "image"],
      ],
      handlers: {
        image: imageHandler,
      },
    },
    "htmlEditButton": {},
    "imageResize": {},
    "clipboard": {
      "matchVisual": false,
      "matchers": [
        ["div.ql-raw-html", (node) => new Delta().insert({ "raw-html": node.innerHTML })],
        ["table", (node) => new Delta().insert({ "raw-html": node.outerHTML })],
      ],
    },
  }), [imageHandler]);

  const formats = ["header", "bold", "italic", "underline", "strike", "blockquote", "font", "list", "bullet", "indent", "link", "image", "color", "align", "height", "width", "background", "raw-html"];

  const preparedValue = useMemo(() => wrapRawHtml(value), [value]);

  return (<>
    <ReactQuill
      ref={quillRef}
      value={preparedValue}
      onChange={onChange}
      modules={modules}
      formats={formats}
    />
    {onSubmit && buttonCopy && (
      <Box display="flex" justifyContent="flex-end" pt={3}>
        <Button variant="contained" color="primary" onClick={onSubmit}>{buttonCopy}</Button>
      </Box>
    )}
  </>);
}

export default Wysiwyg;
